import React from "react";
import styled from "styled-components";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import {
  typographyTokens,
  spacingTokens,
  borderRadiusTokens,
} from "../../config/designTokens";

/**
 * What someone sees when a page fails to load.
 *
 * Before this, an error on a route with no errorElement fell through to
 * React Router's own screen, which shows a stack trace and a note addressed
 * to the developer. Franklin is already the voice the product uses when it
 * has something to say, so he says this too.
 *
 * Two different things are handled here, because they need different
 * instructions. A chunk that fails to load almost always means a deploy
 * landed while the tab was open and the file it is asking for no longer
 * exists; refreshing genuinely fixes it. Anything else is a real fault, and
 * the honest thing is to say so rather than to imply the person did
 * something wrong.
 */

const AVATAR_SRC = "/franklin-avatar.png";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${spacingTokens.xl};
  background-color: ${props => props.theme.background.default};
  text-align: center;
`;

const Avatar = styled.img`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: ${spacingTokens.xl};
  background-color: ${props => props.theme.background.subtle};
`;

const Title = styled.h1`
  font-family: ${typographyTokens.fontFamily.bold};
  font-size: 32px;
  font-weight: ${typographyTokens.fontWeight.bold};
  color: ${props => props.theme.text.primary};
  margin: 0 0 ${spacingTokens.md} 0;
  letter-spacing: -0.01em;
`;

const Description = styled.p`
  font-family: ${typographyTokens.fontFamily.primary};
  font-size: ${typographyTokens.fontSize.body};
  color: ${props => props.theme.text.secondary};
  margin: 0 0 ${spacingTokens.xxl} 0;
  max-width: 44ch;
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  gap: ${spacingTokens.md};
  flex-wrap: wrap;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.body};
  font-weight: ${typographyTokens.fontWeight.medium};
  padding: ${spacingTokens.md} ${spacingTokens.xxl};
  min-height: 48px;
  border: none;
  border-radius: ${borderRadiusTokens.md};
  background-color: ${props => props.theme.brand.primary};
  color: ${props => props.theme.background.default};
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover { opacity: 0.9; }
`;

const SecondaryButton = styled.button`
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.body};
  font-weight: ${typographyTokens.fontWeight.medium};
  padding: ${spacingTokens.md} ${spacingTokens.xxl};
  min-height: 48px;
  border: 1px solid ${props => props.theme.border.default};
  border-radius: ${borderRadiusTokens.md};
  background-color: transparent;
  color: ${props => props.theme.text.primary};
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover { border-color: ${props => props.theme.text.secondary}; }
`;

const Details = styled.details`
  margin-top: ${spacingTokens.xxl};
  max-width: 640px;
  text-align: left;
`;

const Summary = styled.summary`
  font-family: ${typographyTokens.fontFamily.primary};
  font-size: ${typographyTokens.fontSize.caption};
  color: ${props => props.theme.text.secondary};
  cursor: pointer;
`;

const Stack = styled.pre`
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: ${typographyTokens.fontSize.caption};
  color: ${props => props.theme.semantic.negative};
  background-color: ${props => props.theme.background.subtle};
  border: 1px solid ${props => props.theme.border.default};
  border-radius: ${borderRadiusTokens.sm};
  padding: ${spacingTokens.md};
  margin-top: ${spacingTokens.sm};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const name = (error as Error).name;
  const message = (error as Error).message ?? "";
  return (
    name === "ChunkLoadError" ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk") ||
    message.includes("Failed to fetch dynamically imported module")
  );
};

interface FranklinErrorScreenProps {
  error?: unknown;
}

/**
 * The screen itself, given an error rather than reaching for one. Usable
 * anywhere, including from the class ErrorBoundary, which cannot call hooks.
 */
export const FranklinErrorScreen: React.FC<FranklinErrorScreenProps> = ({ error: actual }) => {
  const stale = isChunkLoadError(actual);

  const reload = () => window.location.reload();
  const goHome = () => { window.location.href = "/"; };

  const message = (actual as Error | undefined)?.message;
  const stack = (actual as Error | undefined)?.stack;
  const status = isRouteErrorResponse(actual) ? actual.status : undefined;

  return (
    <Container data-testid="franklin-error-page">
      <Avatar src={AVATAR_SRC} alt="" />

      <Title>{stale ? "Greenback just updated" : "Oops, that did not load"}</Title>

      <Description>
        {stale
          ? "This tab is running an older version of Greenback. Reload to pick up the current one. Nothing has happened to your accounts."
          : "Franklin hit an error opening this page. It has been reported, and your accounts and data are untouched. Reloading usually clears it."}
      </Description>

      <Actions>
        <PrimaryButton onClick={reload}>Reload the page</PrimaryButton>
        <SecondaryButton onClick={goHome}>Go to dashboard</SecondaryButton>
      </Actions>

      {process.env.NODE_ENV === "development" && (message || status) && (
        <Details>
          <Summary>Technical details</Summary>
          <Stack>
            {status ? `Status ${status}\n` : ""}
            {message}
            {stack ? `\n${stack}` : ""}
          </Stack>
        </Details>
      )}
    </Container>
  );
};

/**
 * The route errorElement. Separate from the screen so useRouteError is
 * called unconditionally, at the top level of a component that only ever
 * renders inside a router.
 */
export const FranklinErrorPage: React.FC = () => (
  <FranklinErrorScreen error={useRouteError()} />
);

export default FranklinErrorPage;
